'use client';

import {
  type IUiLinkProps,
  type IUiNavigationApi,
  UiNavigationProvider,
} from '@umodeapporg/ui/components/Navigation/UiNavigationContext';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

const NextLink: React.FC<IUiLinkProps> = ({ to, children, ...rest }) => (
  <Link href={to} {...rest}>
    {children}
  </Link>
);

/** Binds the library navigation port to the Next.js App Router. */
export function UiNextNavigationProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const value = useMemo<IUiNavigationApi>(() => {
    const params = new URLSearchParams(searchParams.toString());
    return {
      location: { pathname, search: params.size ? `?${params.toString()}` : '', state: undefined },
      searchParams: params,
      navigate: (to, options) => {
        if (options?.replace) router.replace(to);
        else router.push(to);
      },
      goBack: () => router.back(),
      setSearchParams: (updater, options) => {
        const next = updater(new URLSearchParams(window.location.search));
        const query = next.toString();
        const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
        if (options?.replace) router.replace(url, { scroll: false });
        else router.push(url, { scroll: false });
      },
      Link: NextLink,
    };
  }, [pathname, searchParams, router]);

  return <UiNavigationProvider value={value}>{children}</UiNavigationProvider>;
}
